import React from 'react';
import { Helmet } from 'react-helmet-async';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="mb-4 text-2xl font-semibold text-slate-900 dark:text-white">{title}</h2>
    <div className="space-y-4 text-slate-600 dark:text-zinc-400">{children}</div>
  </section>
);

const PrivacyPolicy: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - OmniScribe</title>
        <meta name="description" content="OmniScribe Privacy Policy" />
      </Helmet>

      <div className="min-h-screen bg-white py-16 transition-colors duration-300 dark:bg-zinc-950">
        <div className="container mx-auto max-w-4xl px-4">
          <h1 className="h-hero mb-4">Privacy Policy</h1>
          <p className="mb-12 text-slate-500 dark:text-zinc-400">Effective May 9, 2026</p>

          <div className="prose prose-slate max-w-none dark:prose-invert">
            <Section title="Introduction">
              <p>
                Your privacy is important to us. This Privacy Policy explains how NanoUltra, LLC ("Company",
                "we", "our", or "us") collects, uses, stores, and shares information when you use OmniScribe,
                including our website, transcription tools, upload workflows, sharing features, APIs, and
                related services.
              </p>
              <p>
                By using OmniScribe, you agree to the practices described in this Policy. If you do not agree,
                please do not use the service.
              </p>
            </Section>

            <Section title="1. Information We Collect">
              <h3 className="mt-6 text-xl font-medium text-slate-800 dark:text-zinc-200">Information you provide</h3>
              <ul className="list-disc space-y-2 pl-6">
                <li>Account information, such as your name, email address, password, language, and timezone.</li>
                <li>Authentication information from sign-in providers when you choose to use Google sign-in.</li>
                <li>Audio, video, transcript text, URLs, filenames, folders, tags, and other content you upload or create.</li>
                <li>Communications you send to us, including support requests and feedback.</li>
                <li>Billing-related information handled by our payment processor. We do not store full payment card numbers.</li>
              </ul>

              <h3 className="mt-6 text-xl font-medium text-slate-800 dark:text-zinc-200">Information about others</h3>
              <p>
                Audio and video files may contain the personal information or voices of other people. You are
                responsible for obtaining any required consent or permission before uploading or sharing that content.
              </p>

              <h3 className="mt-6 text-xl font-medium text-slate-800 dark:text-zinc-200">Information collected automatically</h3>
              <ul className="list-disc space-y-2 pl-6">
                <li>Usage information, such as uploaded file counts, transcription minutes, selected options, and feature usage.</li>
                <li>Device and log information, such as IP address, browser type, operating system, request metadata, and error logs.</li>
                <li>Approximate location derived from IP address.</li>
                <li>Cookies and local storage used for authentication, preferences, trial usage, and product functionality.</li>
              </ul>
            </Section>

            <Section title="2. How We Use Information">
              <p>We use collected information to:</p>
              <ul className="list-disc space-y-2 pl-6">
                <li>Create, authenticate, and manage your account.</li>
                <li>Provide transcription, translation, summary, export, sharing, billing, and API features.</li>
                <li>Process uploaded audio and video files and generate related outputs.</li>
                <li>Maintain quotas, usage limits, billing records, abuse prevention, and service security.</li>
                <li>Respond to support requests and communicate service updates.</li>
                <li>Analyze and improve service reliability, performance, and product experience.</li>
              </ul>
              <p>
                We do not use your uploaded files or transcripts to train public AI models unless you explicitly
                authorize that use.
              </p>
            </Section>

            <Section title="3. Cookies and Local Storage">
              <p>
                OmniScribe uses cookies, browser storage, and similar technologies to keep you signed in, remember
                preferences such as language and theme, support anonymous trial usage, improve reliability, and
                understand how the product is used.
              </p>
              <p>
                You can control cookies through your browser settings. Some parts of the service may not work
                properly if cookies or local storage are disabled.
              </p>
            </Section>

            <Section title="4. How We Share Information">
              <p>We share information only as needed to operate, secure, and improve OmniScribe, including with:</p>
              <ul className="list-disc space-y-2 pl-6">
                <li>Cloud infrastructure, hosting, storage, and network providers, including Cloudflare services.</li>
                <li>Payment processors such as Stripe for subscription and payment handling.</li>
                <li>Authentication providers such as Google when you choose to sign in or link an account.</li>
                <li>Service providers that support email delivery, analytics, monitoring, security, and AI-assisted processing.</li>
                <li>Other users or public viewers when you intentionally share a transcript or public link.</li>
                <li>Authorities or third parties when required by law, legal process, or to protect rights, safety, and security.</li>
              </ul>
              <p>
                Third-party providers process information according to their own privacy and security practices.
                We aim to share only the information necessary for them to provide services to OmniScribe.
              </p>
            </Section>

            <Section title="5. How We Secure Data">
              <p>
                We use technical and organizational safeguards designed to protect your information, including
                encrypted transport, access controls, monitored infrastructure, and object storage security controls.
                No internet service can be guaranteed to be 100% secure, and you use the service at your own risk.
              </p>
            </Section>

            <Section title="6. Retention and Deletion">
              <p>
                We keep personal information for as long as needed to provide the service, comply with legal
                obligations, resolve disputes, prevent abuse, and maintain business records.
              </p>
              <p>
                When you delete a transcription or your account, we begin removing associated active service data,
                including uploaded files and generated transcripts. Backup copies and system logs may persist for
                a limited period before being deleted through normal retention cycles.
              </p>
            </Section>

            <Section title="7. Your Rights and Choices">
              <p>
                Depending on where you live, you may have rights to access, correct, delete, export, or restrict
                processing of your personal information. You can update some account information directly in
                settings. To make a privacy request, contact us using the details below.
              </p>
              <p>
                You may also unsubscribe from non-essential emails where an unsubscribe option is provided.
              </p>
            </Section>

            <Section title="8. International Transfers">
              <p>
                OmniScribe is operated using global internet infrastructure. Your information may be processed
                and stored in countries other than where you live. By using the service, you understand that your
                information may be transferred to and processed in those locations.
              </p>
            </Section>

            <Section title="9. Children’s Privacy">
              <p>
                OmniScribe is not directed to children under 13, and we do not knowingly collect personal
                information from children under 13. If you believe a child has provided personal information to us,
                please contact us so we can take appropriate action.
              </p>
            </Section>

            <Section title="10. Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time. When we make material changes, we will update
                the effective date above and take reasonable steps to notify users where appropriate.
              </p>
            </Section>

            <Section title="11. Contact Us">
              <p>If you have questions about this Privacy Policy or your data, contact us at:</p>
              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-zinc-800 dark:bg-zinc-900">
                <p className="font-medium text-slate-700 dark:text-zinc-300">NanoUltra, LLC</p>
                <p className="text-slate-600 dark:text-zinc-400">Delaware LLC</p>
                <p className="text-slate-600 dark:text-zinc-400">Founded January 13, 2026</p>
                <p className="text-slate-600 dark:text-zinc-400">Delaware File Number 10470896</p>
                <p className="text-slate-600 dark:text-zinc-400">OmniScribe</p>
                <p className="text-slate-600 dark:text-zinc-400">privacy@omniscribe.ai</p>
              </div>
            </Section>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
